import type { Order } from "@/services/types/orders";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import * as XLSX from "xlsx";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getChannelName(source: string): string {
  switch (source) {
    case "WH":
      return "WhatsApp";
    case "EM":
      return "Email";
    case "KS":
      return "Kaso";
    case "SP":
      return "Supy";
    default:
      return source;
  }
}

function prepareOrderData(orders: Order[]) {
  // Define headers based on the order structure
  const headers = [
    "Order ID",
    "Status",
    "Channel",
    "Date",
    "Restaurant Name",
    "Restaurant Address",
    "Order Items",
    "Expected Delivery Date",
    "Restaurant Branch",
    "Branch Address",
    "Supplier",
  ];

  // Convert orders to rows
  const rows = orders.map((order) => {
    // Format order details as a string
    const orderDetails = Array.isArray(order.order_details)
      ? order.order_details
          .map((item) => `${item.name} (${item.quantity} ${item.uom})`)
          .join("; ")
      : "";

    return [
      order.id,
      order.status,
      order.source,
      new Date(order.order_datetime).toLocaleDateString(),
      order.restaurant_name,
      order.restaurant_address,
      orderDetails,
      order.expected_delivery_date
        ? new Date(order.expected_delivery_date).toLocaleDateString()
        : "",
      order.restaurant_branch || "",
      order.branch_address,
      // order.supplier,
    ];
  });

  return { headers, rows };
}

export function exportOrdersToCSV(orders: Order[]) {
  // short preprocess for source full name
  const ordersWithSource = orders.map((order) => ({
    ...order,
    source: getChannelName(order.source),
  }));

  const { headers, rows } = prepareOrderData(ordersWithSource);

  // Combine headers and rows
  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row
        .map((cell) => {
          // Escape commas and quotes in cell values
          const cellStr = String(cell);
          if (cellStr.includes(",") || cellStr.includes('"')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(","),
    ),
  ].join("\n");

  // Create and trigger download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `orders_${new Date().toISOString().split("T")[0]}.csv`,
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportOrdersToExcel(orders: Order[]) {
  // short preprocess for source full name
  const ordersWithSource = orders.map((order) => ({
    ...order,
    source: getChannelName(order.source),
  }));

  const { headers, rows } = prepareOrderData(ordersWithSource);

  // Create a new workbook
  const wb = XLSX.utils.book_new();

  // Create a worksheet from the data
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  // Set column widths
  const colWidths = headers.map((header) => ({
    wch: Math.max(header.length, 20),
  }));
  ws["!cols"] = colWidths;

  // Add the worksheet to the workbook
  XLSX.utils.book_append_sheet(wb, ws, "Orders");

  // Generate Excel file
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  // Create and trigger download
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `orders_${new Date().toISOString().split("T")[0]}.xlsx`,
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
